import { useCallback, useEffect, useState } from 'react';

import type { CreateEnvironmentRequest, EnvironmentSummary } from '@/api/configuration';
import {
    createEnvironment,
    deleteEnvironment,
    listEnvironments,
    updateEnvironment,
} from '@/api/configuration';

// --- Environment Form ---

function EnvironmentForm({
  initial,
  nextPriority,
  onSave,
  onCancel,
}: {
  initial?: EnvironmentSummary & { priority?: number; url?: string };
  nextPriority: number;
  onSave: (data: CreateEnvironmentRequest) => void;
  onCancel: () => void;
}) {
  const [id, setId] = useState(initial?.id ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [priority, setPriority] = useState(initial?.priority?.toString() ?? nextPriority.toString());
  const [url, setUrl] = useState(initial?.url ?? '');
  // AWS cross-account access (all optional)
  const [awsAuthType, setAwsAuthType] = useState<'none' | 'role' | 'static'>(initial?.awsAuthType ?? 'none');
  const [awsAccountId, setAwsAccountId] = useState(initial?.awsAccountId ?? '');
  const [awsAssumeRoleArn, setAwsAssumeRoleArn] = useState(initial?.awsAssumeRoleArn ?? '');
  const [awsExternalId, setAwsExternalId] = useState(initial?.awsExternalId ?? '');
  const [awsDefaultRegion, setAwsDefaultRegion] = useState(initial?.awsDefaultRegion ?? '');
  const [awsAccessKeyId, setAwsAccessKeyId] = useState(initial?.awsAccessKeyId ?? '');
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState(initial?.awsSecretAccessKey ?? '');
  const [awsProfileName, setAwsProfileName] = useState(initial?.awsProfileName ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      id: id.trim().toLowerCase().replace(/\s+/g, '-'),
      name: name.trim(),
      priority: parseInt(priority, 10) || nextPriority,
      url: url.trim() || undefined,
      awsAuthType: awsAuthType !== 'none' ? awsAuthType : undefined,
      awsAccountId: awsAccountId.trim() || undefined,
      awsDefaultRegion: awsDefaultRegion.trim() || undefined,
      awsAssumeRoleArn: awsAuthType === 'role' ? (awsAssumeRoleArn.trim() || undefined) : undefined,
      awsExternalId: awsAuthType === 'role' ? (awsExternalId.trim() || undefined) : undefined,
      awsAccessKeyId: awsAuthType === 'static' ? (awsAccessKeyId.trim() || undefined) : undefined,
      awsSecretAccessKey: awsAuthType === 'static' ? (awsSecretAccessKey.trim() || undefined) : undefined,
      awsProfileName: awsAuthType === 'static' ? (awsProfileName.trim() || undefined) : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-surface-raised p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="env-id" className="block text-xs font-medium text-content-secondary mb-1">
            ID * <span className="text-content-tertiary">(e.g. dev, staging, prod)</span>
          </label>
          <input
            id="env-id"
            type="text"
            required
            value={id}
            onChange={(e) => setId(e.target.value)}
            disabled={!!initial}
            placeholder="production"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
        <div>
          <label htmlFor="env-name" className="block text-xs font-medium text-content-secondary mb-1">
            Display Name *
          </label>
          <input
            id="env-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Production"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>
        <div>
          <label htmlFor="env-order" className="block text-xs font-medium text-content-secondary mb-1">
            Priority <span className="text-content-tertiary">(lower = displayed first)</span>
          </label>
          <input
            id="env-order"
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            min={0}
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>
        <div>
          <label htmlFor="env-url" className="block text-xs font-medium text-content-secondary mb-1">
            URL <span className="text-content-tertiary">(optional)</span>
          </label>
          <input
            id="env-url"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://staging.example.com"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>
      </div>
      {/* AWS Access (optional) */}
      <details className="group" open={awsAuthType !== 'none'}>
        <summary className="cursor-pointer text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2 select-none">
          AWS Access <span className="text-content-tertiary font-normal normal-case tracking-normal">(optional — for cross-account EC2 management)</span>
        </summary>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label htmlFor="env-aws-auth-type" className="block text-xs font-medium text-content-secondary mb-1">Auth Method</label>
              <select
                id="env-aws-auth-type"
                value={awsAuthType}
                onChange={(e) => setAwsAuthType(e.target.value as 'none' | 'role' | 'static')}
                className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
              >
                <option value="none">None (use default)</option>
                <option value="role">IAM Role Assumption</option>
                <option value="static">Static Credentials</option>
              </select>
            </div>
            <div>
              <label htmlFor="env-aws-account-id" className="block text-xs font-medium text-content-secondary mb-1">Account ID</label>
              <input id="env-aws-account-id" type="text" value={awsAccountId} onChange={(e) => setAwsAccountId(e.target.value)} placeholder="123456789012" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
            </div>
            <div>
              <label htmlFor="env-aws-default-region" className="block text-xs font-medium text-content-secondary mb-1">Default Region</label>
              <input id="env-aws-default-region" type="text" value={awsDefaultRegion} onChange={(e) => setAwsDefaultRegion(e.target.value)} placeholder="eu-west-2" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
            </div>
          </div>

          {/* Role Assumption fields */}
          {awsAuthType === 'role' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="env-aws-role-arn" className="block text-xs font-medium text-content-secondary mb-1">Assume Role ARN *</label>
                <input id="env-aws-role-arn" type="text" required value={awsAssumeRoleArn} onChange={(e) => setAwsAssumeRoleArn(e.target.value)} placeholder="arn:aws:iam::123456789012:role/CorvusAccess" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
              </div>
              <div>
                <label htmlFor="env-aws-external-id" className="block text-xs font-medium text-content-secondary mb-1">External ID</label>
                <input id="env-aws-external-id" type="text" value={awsExternalId} onChange={(e) => setAwsExternalId(e.target.value)} placeholder="Optional security token" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
              </div>
            </div>
          )}

          {/* Static Credentials fields */}
          {awsAuthType === 'static' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label htmlFor="env-aws-profile-name" className="block text-xs font-medium text-content-secondary mb-1">Profile Name *</label>
                <input id="env-aws-profile-name" type="text" required value={awsProfileName} onChange={(e) => setAwsProfileName(e.target.value)} placeholder="client2" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600" />
              </div>
              <div>
                <label htmlFor="env-aws-access-key" className="block text-xs font-medium text-content-secondary mb-1">Access Key ID *</label>
                <input id="env-aws-access-key" type="text" required value={awsAccessKeyId} onChange={(e) => setAwsAccessKeyId(e.target.value)} placeholder="AKIA..." className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content font-mono focus:outline-none focus:ring-2 focus:ring-brand-600" />
              </div>
              <div>
                <label htmlFor="env-aws-secret-key" className="block text-xs font-medium text-content-secondary mb-1">Secret Access Key *</label>
                <input id="env-aws-secret-key" type="password" required value={awsSecretAccessKey} onChange={(e) => setAwsSecretAccessKey(e.target.value)} placeholder="••••••••" className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content font-mono focus:outline-none focus:ring-2 focus:ring-brand-600" />
              </div>
            </div>
          )}
        </div>
      </details>

      <div className="flex gap-2 pt-1">
        <button type="submit" className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          {initial ? 'Update' : 'Add Environment'}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// --- Main Component ---

interface EnvironmentManagerProps {
  projectKey: string;
}

export function EnvironmentManager({ projectKey }: EnvironmentManagerProps) {
  const [environments, setEnvironments] = useState<EnvironmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<EnvironmentSummary | null>(null);

  const loadEnvironments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listEnvironments(projectKey);
      setEnvironments(data);
    } catch {
      setError('Failed to load environments.');
    } finally {
      setLoading(false);
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectKey) {
      loadEnvironments();
    }
  }, [projectKey, loadEnvironments]);

  const handleAdd = async (data: CreateEnvironmentRequest) => {
    try {
      await createEnvironment(projectKey, data);
      setShowForm(false);
      loadEnvironments();
    } catch {
      setError('Failed to create environment.');
    }
  };

  const handleEdit = async (data: CreateEnvironmentRequest) => {
    if (!editing) return;
    try {
      await updateEnvironment(projectKey, editing.id, data);
      setEditing(null);
      loadEnvironments();
    } catch {
      setError('Failed to update environment.');
    }
  };

  const handleDelete = async (env: EnvironmentSummary) => {
    if (!confirm(`Delete environment "${env.name}"? This will also delete all associated servers, services, artifacts, and other configuration.`)) return;
    try {
      await deleteEnvironment(projectKey, env.id);
      loadEnvironments();
    } catch {
      setError('Failed to delete environment.');
    }
  };

  const nextPriority = environments.length > 0
    ? Math.max(...environments.map((e) => e.priority ?? 0)) + 1
    : 0;

  return (
    <section className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-content">Environments</h3>
        {!showForm && !editing && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-800 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Environment
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 mb-3">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button onClick={() => { setError(null); loadEnvironments(); }} className="mt-1 text-xs font-medium text-red-700 dark:text-red-300 underline">Retry</button>
        </div>
      )}

      {loading && (
        <div className="animate-pulse space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
      )}

      {showForm && (
        <div className="mb-3">
          <EnvironmentForm nextPriority={nextPriority} onSave={handleAdd} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {!loading && environments.length === 0 && !showForm && (
        <div className="rounded-lg border border-dashed border-border p-4 text-center">
          <p className="text-sm text-content-tertiary">No environments configured. Add one to enable deployments.</p>
        </div>
      )}

      {!loading && environments.length > 0 && (
        <ul className="space-y-2">
          {environments.map((env) =>
            editing?.id === env.id ? (
              <li key={env.id}>
                <EnvironmentForm
                  initial={env as EnvironmentSummary & { priority?: number; url?: string }}
                  nextPriority={nextPriority}
                  onSave={handleEdit}
                  onCancel={() => setEditing(null)}
                />
              </li>
            ) : (
              <li key={env.id} className="rounded-lg border border-border p-3 flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-content">{env.name}</span>
                  <span className="ml-2 text-xs text-content-tertiary font-mono">{env.id}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(env)}
                    className="rounded px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(env)}
                    className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                  >
                    Remove
                  </button>
                </div>
              </li>
            )
          )}
        </ul>
      )}
    </section>
  );
}
